import React, { useState, useRef } from "react";
import Icon from "../Icon/Icon";
import "../../blocks/CreateCommunicationModal/CreateCommunicationModal.scss";
import LoaderMin from "components/blocks/Loader/LoaderMin";

interface IFormCreateCommunication {
  title?: string;
  onSubmit: (dataForm: IDataForm) => void;
  onClose?: () => void;
  loadingProcess?: boolean;
  subject?: string;
  idComunication?: string;
}

interface IDataForm {
  asunto: string;
  mensaje: string;
  files: File[];
  idComunication: string;
}

const ALLOWED_EXTENSIONS = [
  "pdf", "doc", "docx", "zip", "7z", "txt",
  "xls", "xlsx", "csv", "odt", "ods",
  "jpg", "jpeg", "png", "webp",
];
const PROHIBITED_EXTENSIONS = [
  "exe", "bat", "cmd", "sh", "msi", "vbs", "scr", "js", "jar",
  "html", "htm", "mhtml", "jfif", "pptx",
];
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

const validateFileExtension = (fileName: string): boolean => {
  const lastDot = fileName.lastIndexOf(".");
  if (lastDot === -1 || lastDot === fileName.length - 1) return false;
  const ext = fileName.substring(lastDot + 1).toLowerCase();
  if (!ext.trim()) return false;
  if (PROHIBITED_EXTENSIONS.includes(ext)) return false;
  return ALLOWED_EXTENSIONS.includes(ext);
};

const isReply = (title?: string) => title === "Responder";

const FormCreateCommunication: React.FC<IFormCreateCommunication> = ({
  title,
  onSubmit,
  onClose,
  loadingProcess,
  subject,
  idComunication,
}) => {
  const reply = isReply(title);

  const [dataForm, setDataForm] = useState<IDataForm>({
    asunto: reply ? `Respuesta  ${subject ?? ""}` : "",
    mensaje: "",
    files: [],
    idComunication: idComunication || "",
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [maxSizeError, setMaxSizeError]       = useState(false);
  const [invalidExtError, setInvalidExtError] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setDataForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMaxSizeError(false);
    setInvalidExtError(false);
    const newFiles = e.target.files;
    if (!newFiles) return;

    const updated = [...selectedFiles];
    for (let i = 0; i < newFiles.length; i++) {
      const file = newFiles[i];
      if (file.size > MAX_FILE_SIZE_BYTES) { setMaxSizeError(true); e.target.value = ""; return; }
      if (!validateFileExtension(file.name)) { setInvalidExtError(true); e.target.value = ""; return; }
      updated.push(file);
    }
    setSelectedFiles(updated);
    setDataForm((prev) => ({ ...prev, files: updated }));
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    const updated = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(updated);
    setDataForm((prev) => ({ ...prev, files: updated }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(dataForm);
  };

  const modalTitle  = title || "Crear comunicación";
  const modalSubtitle = reply
    ? "Escribe tu respuesta y adjunta los documentos que necesites."
    : "Envíanos tu consulta y tu gestor te responderá lo antes posible.";

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="comm-modal" onClick={(e) => e.stopPropagation()}>
        {onClose && (
          <button className="comm-modal__close" aria-label="Cerrar" onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        )}

        {loadingProcess && <LoaderMin />}

        <header className="comm-modal__head">
        <div className="comm-modal__icon">
          <Icon name={reply ? "chat" : "chat"} size={22} />
        </div>
        <h2 className="comm-modal__title">{modalTitle}</h2>
        <p className="comm-modal__sub">{modalSubtitle}</p>
      </header>

      <form className="comm-form" onSubmit={handleSubmit}>
        <div className="field">
          <label className="field__label" htmlFor="fc-asunto">Asunto</label>
          <input
            id="fc-asunto"
            type="text"
            className="field__input"
            name="asunto"
            placeholder="Escribe un asunto claro y breve"
            value={dataForm.asunto}
            onChange={handleInputChange}
            readOnly={reply}
            required
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="fc-mensaje">Mensaje</label>
          <textarea
            id="fc-mensaje"
            className="field__input field__textarea"
            name="mensaje"
            placeholder="Escribe tu comentario..."
            rows={6}
            value={dataForm.mensaje}
            onChange={handleInputChange}
            required
          />
          <div className="field__hint">
            <Icon name="info" size={13} /> Sé lo más específico posible para agilizar la respuesta.
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".jpg,.jpeg,.png,.gif,.pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.zip,.7z,.txt,.csv,.odt,.ods,.webp"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {selectedFiles.length > 0 && (
          <div className="comm-form__files">
            {selectedFiles.map((file, i) => (
              <div key={i} className="comm-form__file">
                <Icon name="file" size={16} />
                <span className="comm-form__file-name">{file.name}</span>
                <button
                  type="button"
                  className="comm-form__file-x"
                  aria-label="Quitar archivo"
                  onClick={() => removeFile(i)}
                >
                  <Icon name="x" size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {maxSizeError && (
          <p className="form-error" style={{ marginBottom: "var(--s-4)" }}>
            Algunos archivos superan el tamaño máximo de 100 MB por archivo.
          </p>
        )}
        {invalidExtError && (
          <p className="form-error" style={{ marginBottom: "var(--s-4)" }}>
            Extensión no permitida. Formatos aceptados: PDF, DOC, DOCX, ZIP, 7Z, TXT, XLS, XLSX, CSV, ODT, ODS, JPG, JPEG, PNG, WEBP.
          </p>
        )}

        <footer className="comm-modal__actions">
          {onClose && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={onClose}>
              Cancelar
            </button>
          )}
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => fileInputRef.current?.click()}
          >
            <Icon name="paperclip" size={16} /> Adjuntar archivos
          </button>
          <button type="submit" className="btn btn--primary btn--sm" disabled={loadingProcess}>
            Enviar <Icon name="arrow-right" size={16} />
          </button>
        </footer>
      </form>
    </div>
  </div>
  );
};

export default FormCreateCommunication;
